import type {
  BulkFindingSeverity,
  BulkObject,
  BulkValidationFinding,
  BulkValidationSummary,
} from "../../api/BulkAPI.types";

export interface BulkFlatFinding {
  field: string;
  code: string;
  severity: BulkFindingSeverity;
  valueText: string | null;
  expectedText: string | null;
  language: string | null;
}

function toText(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(toText).join(", ");
  }

  if (value === null || value === undefined) {
    return "";
  }

  return JSON.stringify(value);
}

function toOptionalText(value: unknown): string | null {
  const text = toText(value);

  return text === "" ? null : text;
}

function normalizeSeverity(severity: string): BulkFindingSeverity {
  return severity === "warning" ? "warning" : "error";
}

function flattenFindings(
  findingList: BulkValidationFinding[],
  language: string | null,
): BulkFlatFinding[] {
  return findingList.map(({ code, expected, field, severity, value }) => ({
    field,
    code,
    severity: normalizeSeverity(severity),
    valueText: toOptionalText(value),
    expectedText: toOptionalText(expected),
    language,
  }));
}

export function flattenBulkObjectFindings(
  object: BulkObject,
): BulkFlatFinding[] {
  return [
    ...flattenFindings(object.validation.findings, null),
    ...object.children.flatMap((child) =>
      flattenFindings(child.validation.findings, child.data.language),
    ),
  ];
}

export function countBulkFindings(
  findingList: BulkFlatFinding[],
): BulkValidationSummary {
  const warnings = findingList.filter(
    ({ severity }) => severity === "warning",
  ).length;

  return { errors: findingList.length - warnings, warnings };
}
